import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Commands, Results } from '@local/client-contracts';
import { DragData, DragMetadata } from '@shared/directives/drag-and-drop.directive';
import { HighlightStyle } from '@shared/pipes';
import { LogService } from '@shared/services';
import { isMiddleClick } from '@shared/utils';
import { Logger } from '@unleash-tech/js-logger';
import { HubService } from 'src/app/bar/services/hub.service';
import { CommandContext, SourceClick, TelemetryTrigger } from '../../models/results-types';
import { UiIconModel } from '@local/ui-infra';
import { getIconModel } from '@shared/utils/set-icon.util';

export type TooltipPosition = 'bottom' | 'top' | 'right' | 'left';
@Component({
  selector: 'result-title',
  templateUrl: './result-title.component.html',
  styleUrls: ['./result-title.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultTitleComponent implements OnInit {
  private readonly source: SourceClick = 'title';
  private _model: Results.Title;
  private logger: Logger;
  highlightStyle: HighlightStyle = 'default';
  dragData: DragData;
  iconModel: UiIconModel;

  @Input() mainWidth = 1;
  @Input() showSingleLineView: boolean;
  @Input() highlights: Array<string>;
  @Input() displayTooltip = true;
  @Input() displayTooltipIfCut = true;
  @Input() showInlineTooltip = false;
  @Input() tooltipPosition: TooltipPosition = 'bottom';
  @Input() selected: boolean;
  @Input() isClickable: boolean;
  @Input() set dragMetadata(metadata: DragMetadata) {
    this.dragData = { metadata, model: this.model };
  }
  @Input() set model(val: Results.Title) {
    this._model = val;
    this.iconModel = getIconModel(this.model?.icon);
  }
  @Output() invoke = new EventEmitter<{
    command: Commands.Command;
    context: Partial<CommandContext>;
    trigger: TelemetryTrigger;
    source: SourceClick;
  }>();

  @ViewChild('resultTitle', { read: ElementRef }) titleRef: ElementRef;

  get model(): Results.Title {
    return this._model;
  }

  constructor(private logService: LogService, private hubService: HubService) { }

  ngOnInit() {
    this.logger = this.logService.scope(`ResultTitle - ${this.model?.text?.substring(0, 10)}`);
  }

  @HostBinding('class.not-one-line') get oneLineDisplay() {
    return !this.showSingleLineView;
  }

  @HostListener('contextmenu', ['$event'])
  onRightClick(event: MouseEvent) {
    if (this.model?.onRightClick) {
      this.sendCommand(this.model.onRightClick, null, 'mouse_click');
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }

  @HostListener('auxclick', ['$event']) onAuxClick(event: MouseEvent) {
    if (isMiddleClick(event)) {
      event.stopImmediatePropagation();
      return this.onClick(event);
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (this.model?.onClick || this.isClickable) {
      this.sendCommand(this.model?.onClick, null, 'mouse_click');
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }

  @HostBinding('class.clickable') get clickable() {
    return !!this.model?.onClick;
  }

  private sendCommand(command: Commands.Command, context: Partial<CommandContext> = {}, trigger?: TelemetryTrigger) {
    if (!command && !this.isClickable) return;
    this.invoke.emit({ command, context, trigger, source: this.source });
  }

  get text() {
    return this.model.text ?? this.model.tooltip;
  }

  get tooltipText(): string {
    if (this.showInlineTooltip) {
      return this.model.tooltip;
    }
    const el: HTMLElement = this.titleRef?.nativeElement?.querySelector('#text');
    if (!this.displayTooltip || !el) {
      return '';
    }
    if (this.displayTooltipIfCut && el?.offsetWidth < el?.scrollWidth) {
      return this.model.tooltip || this.model.text;
    }
    return '';
  }
}
